import { isC3Experience } from "../../shared/util/C3Utils";
define((require) => {
    const Backbone                 = require("backbone");
    const Browser                  = require("shared/services/Browser");
    const channelBridge            = require("shared/ui/viewer/ChannelBridge");
    const CSSHistoryManager        = require("services/CSSHistoryManager");
    const EditorRouter             = require("services/router/EditorRouter");
    const EditorStatsDAnalytics    = require("services/tracker/EditorStatsDAnalytics");
    const ErrorAction              = require("editor/services/error/ErrorAction");
    const EditorErrorLogger        = require("editor/services/error/EditorErrorLogger");
    const ErrorHandler             = require("shared/services/error/ErrorHandler");
    const FontLoader               = require("shared/util/FontLoader");
    const Injector                 = require("shared/util/Injector");
    const LoggedIn                 = require("shared/security/LoggedIn");
    const Marionette               = require("marionette");
    const NotificationBar          = require("editor/ui/notificationbar/NotificationBar");
    const Preconditions            = require("shared/util/Preconditions");
    const ProjectSelector          = require("ui/projects/ProjectSelector");
    const RestApi                  = require("shared/services/RestApi");
    const TipManager               = require("services/TipManager");
    const ViewScope                = require("shared/ui/common/ViewScope");
    const Window                   = require("shared/services/Window");
    const WidgetTypes              = require("shared/ui/experience/widgets/WidgetTypes");

    // Require less file used by this application.
    require("main.less");

    /**
     * The editor application.
     *
     * @class EditorApplication
     */
    const EditorApplication = Marionette.Application.extend({

        /**
         * Start the application.
         *
         * Loads the default font kit from Typekit before proceeding.
         *
         * @param  {Object} config The raw configuration object.
         */
        start: function(config) {
            FontLoader.typekit("eka2gcy")
                .always(() => {
                    Marionette.Application.prototype.start.call(this, config);
                });
        }
    });

    const app = new EditorApplication(),
        statsDName = "statsD";

    app.addRegions({
        navBarRegion: ".navigation-bar",
        projectsRegion: ".projects-panel",
        mainRegion: ".main"
    });

    app.addInitializer(({statsDBucket}) => {
        Injector.register(statsDName, new EditorStatsDAnalytics(statsDBucket));
    });


    /**
    * Circumvent circular dependency issues in require.js
    * required in composite widgets by allowing injection of the necessary classes.
    */
    app.addInitializer(() => {
        Injector.register("WidgetTypes", WidgetTypes);
    });

    /**
     * Setup error logger and handler as the first thing.
     */
    app.addInitializer((config) => {
        // Ensure the error handler to report via the NotificationBar...
        ErrorHandler.notify = {
            show: (error, options) => {
                return NotificationBar.error(error, options);
            },

            discard: (options) => {
                if (typeof options === "string") {
                    options = {id: options, clear: "self"};
                }

                NotificationBar.close(options);
            }
        };
        // Create the error logger. We will associate our statsD instance once it is initialised later on...
        const errorLogger = new EditorErrorLogger(config);
        // Determine if it should be enabled
        errorLogger.enabled = config.trackErrors;

        // Hook in statsD to relevant error classes...
        const statsD = Injector.get(statsDName);
        errorLogger.statsD(statsD);
        ErrorAction.statsD(statsD);

        // Pass the logger to the Preconditions instance
        Preconditions.setErrorLogger(errorLogger);
        Injector.register("errorLogger", errorLogger);
    });

    /**
     * Initialize the database layer.
     */
    app.addInitializer((config) => {
        RestApi.init(config.adminApiBaseUrl, config.proxyUrl);
    });

    app.addInitializer(() => {
        app.tipManager = new TipManager();
    });

    app.addInitializer(() => {
        // Disable the spell check for IE, see bug report MOS-2185
        if (Browser.type.ie) {
            Browser.disableNativeSpellChecker();
        }
    });

    app.addInitializer(() => {
        Injector.register("mainRegion", app.mainRegion);
        Injector.register("navBarRegion", app.navBarRegion);
        Injector.register("history", Backbone.history);

        // Set up re-render on regions in case the logged in principal changes the active role or state, to ensure
        // that all UI elements testing on privileges will reflect the update. We do not bother to test for logout,
        // i.e. no principal, since that is already handled (and displaying the sign in page again)...
        LoggedIn.on("change:login", (principal) => {
            const updated = principal && principal.get("updated");

            if (updated) {
                // Main View is not ready when coming from confirm link, but gets instantiated anyway by the Application,
                // thus we will show it here only if current view is available.
                if (app.mainRegion.currentView) {
                    app.mainRegion.show(app.mainRegion.currentView);
                }

                app.navBarRegion.show(app.navBarRegion.currentView);
            }
        });
        // Re-render top nav bar if the backing user changes...
        LoggedIn.on("change:user", () => {
            app.navBarRegion.show(app.navBarRegion.currentView);
        });
    });

    /**
     * Start the router, which will cause the initial page to load.
     */
    app.addInitializer(() => {
        new EditorRouter().start();

        Backbone.history.start();

        const manager = new CSSHistoryManager(); // Google canary does not like new Foo().something() - seems like an anonymous instance is sometimes troublesome (optimised away?)... :/
        manager.start();
    });

    /**
     * Set up editor analytics.
     */
    app.addInitializer(() => {
        const statsD = Injector.get(statsDName);

        statsD.trackPageView();
        statsD.trackInitTime(Date.now() - window.startTime); // TODO: bad global state, move under "__zmags" or something
    });

    /**
     * Ensure the channel bridge is *not* available in the editor for viewer preview for embedded
     * Experiences and Groups (to avoid loading channels.js in the Editor); the publishable embed
     * widget will simply use the direct url when in preview mode.
     */
    app.addInitializer(() => {
        const init = channelBridge();

        // Disable channel bridge for good while in the editor!
        init(); // no Controller!
    });


    app.addInitializer(() => {
        // As Projects have been deprecated for C3 experiences, we need to disable this logic.
        if(!isC3Experience()) ProjectSelector.start(app.projectsRegion);
    });

    /**
     * Setup top level view scope.
     */
    app.addInitializer((config) => {
        ViewScope.setTopLevelViewScope({

            showNotification: (content, options) => {
                NotificationBar.open(content, options);
            },

            hideNotification: (force) => {
                NotificationBar.close(force);
            },

            toggleProjectSelector: (toggle) => {
                ProjectSelector.toggleProjectSelector(toggle);
            }

        });

        // Have to set statsD this awkward way because the NotificationBar is a singleton already constructed on load...
        const statsD = Injector.get(statsDName);
        NotificationBar.options.statsD = statsD;

        // Notify the user if the browser is not supported...
        if (!Browser.capabilities.isSupported) {
            const options = {
                tag: "unsupported_browser",
                closeable: true,
                ignoreDuplicates: true,
                flash: {duplicate: false},
                events: {
                    "click #browsersupport": (event) => {
                        // Go to the community when logged in to avoid breaking the normal work flow...
                        if (LoggedIn.principal()) {
                            event.preventDefault();
                            Window.open(config.communityBaseUrl + "/hc/en-us/articles/200588918", "zmags_creator_community");
                        }

                        statsD.track("notificationbar.click.browsersupport");
                    }
                }
            };
            // Tag this as an error merely to get the desired styling...
            NotificationBar.error("You are using an unsupported browser - performance and correctness may suffer!&nbsp;&nbsp;&nbsp;" +
                                  "See <a id='browsersupport' href='#browsersupport'>Browser Support</a> for further information.", options);
        }
    });

    return app;
});



// WEBPACK FOOTER //
// ./src/editor/app/EditorApplication.js